import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader,
  FileText,
} from "lucide-react";
import React, { useState, useMemo } from "react";
import { RESPONSIVE_CLASSES } from "~/utils/responsive";
import { useInvoices } from "~/services/billing/billing.queries";
import { useAuth } from "~/store/auth.store";
import { getInvoicePdf } from "~/services/billing/billing.api";
import { Invoice as ApiInvoice } from "~/components/admin/billing/types";
import {
  getQuickBooksInvoice,
  getQuickBooksPaymentLink,
} from "~/services/quickbooks/quickbooks.api";
import { useGetAllMyRelatedBookings } from "~/services/booking";
import { TBooking, TServiceTicket } from "~/types";
import { ServiceType, JobStatus } from "~/utils/constants";
import { calculateServiceCost } from "~/utils/service-cost-calculator";
import { useGetStaffRoleByName } from "~/services/common";
import { useQuery } from "@tanstack/react-query";
import { addOnServicesApi } from "~/services/add-on-services/add-on-services.api";
import { useGetAdminCharges } from "~/services/admin-charge";

export interface BillingAccount {
  status: "active" | "inactive";
  balance: number;
  overdue: number;
}

export interface InvoiceItem {
  description: string;
  serviceDate?: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface Invoice {
  id: string;
  bookingRef?: string;
  project?: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "paid" | "unpaid" | "overdue";
  quickbooksLink?: string;
  paidDate?: string;
  items: InvoiceItem[];
  notes?: string[];
}

export interface BillingData {
  account: BillingAccount;
  invoices: Invoice[];
}

export const BillingView: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>("all");
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [expandedEstimate, setExpandedEstimate] = useState<string | null>(null);
  const { user } = useAuth();

  // Calculate startDate based on timeRange
  const startDate = useMemo(() => {
    const now = new Date();
    switch (timeRange) {
      case "year":
        return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString();
      case "6months":
        return new Date(now.setMonth(now.getMonth() - 6)).toISOString();
      case "3months":
        return new Date(now.setMonth(now.getMonth() - 3)).toISOString();
      default:
        return undefined;
    }
  }, [timeRange]);

  // Fetch invoice list from API
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    error: invoicesError,
  } = useInvoices({
    clientId: user?.client?.id,
    startDate: startDate,
    limit: 50,
  });

  // Fetch bookings for the client to get ESTIMATED services
  const {
    data: bookingsData,
    isLoading: isLoadingBookings,
    error: bookingsError,
  } = useGetAllMyRelatedBookings({
    page: 1,
    pageSize: 100,
  });

  // Get Art Handler role data for staff rates (use first booking's clientId if available)
  const firstBookingClientId = bookingsData?.items?.[0]?.clientId;
  const { data: artHandlerRole } = useGetStaffRoleByName("Art Handler", firstBookingClientId);

  // Get add-on services data for pricing
  const { data: addOnServices } = useQuery({
    queryKey: ["add-on-services"],
    queryFn: () => addOnServicesApi.getAll(),
  });

  // Get admin charges data for looking up charge names
  const { data: adminChargesData } = useGetAdminCharges({ params: {} });
  const adminChargesMap = useMemo(() => {
    const map = new Map<string, { name: string; rate?: number }>();
    adminChargesData?.items?.forEach((charge) => {
      map.set(charge.id, { name: charge.name, rate: charge.rate });
    });
    return map;
  }, [adminChargesData]);

  // Convert API data to UI format
  const billingData = useMemo(() => {
    if (!invoicesData) return null;

    // Calculate total balance and overdue amount
    let totalBalance = 0;
    let overdueAmount = 0;

    const mappedInvoices = invoicesData.invoices.map((invoice: ApiInvoice) => {
      // Determine invoice status for UI
      let uiStatus: "paid" | "unpaid" | "overdue" = "unpaid";
      if (invoice.status === "paid") {
        uiStatus = "paid";
      } else if (invoice.status === "overdue") {
        uiStatus = "overdue";
        overdueAmount += invoice.amount;
      } else if (["sent", "pending"].includes(invoice.status)) {
        uiStatus = "unpaid";
        totalBalance += invoice.amount;
      }

      // Create QuickBooks payment link if available
      const quickbooksLink = invoice.quickbooksInvoiceId
        ? `https://quickbooks.intuit.com/payments/invoice/${invoice.quickbooksInvoiceId}`
        : undefined;

      // Get invoice details from backend (mocked)
      const invoiceDetails = {
        bookingRef: undefined,
        project: undefined,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10),
        paidDate: undefined,
        items: [
          {
            description: "Service",
            serviceDate: invoice.date,
            quantity: 1,
            rate: invoice.amount,
            amount: invoice.amount,
          },
        ],
        notes: [],
      };

      return {
        id: invoice.invoiceNumber || invoice.id,
        date: invoice.date
          ? new Date(invoice.date).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),
        amount: invoice.amount,
        status: uiStatus,
        quickbooksLink,
        quickbooksInvoiceId: invoice.quickbooksInvoiceId,
        ...invoiceDetails,
      };
    });

    return {
      account: {
        status: "active",
        balance: totalBalance,
        overdue: overdueAmount,
      },
      invoices: mappedInvoices,
    };
  }, [invoicesData]);

  // Function to download invoice PDF
  const handleDownloadPdf = async (invoiceId: string) => {
    try {
      const pdfBlob = await getInvoicePdf(invoiceId);

      // Create URL for blob and open in new tab
      const pdfUrl = URL.createObjectURL(
        new Blob([pdfBlob], { type: "application/pdf" })
      );
      window.open(pdfUrl, "_blank");

      // Release URL after 30 seconds
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 30000);
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
    }
  };

  // Function to get payment link for an invoice
  const handleGetPaymentLink = async (
    invoiceId: string,
    quickbooksInvoiceId?: string
  ) => {
    try {
      // If we already have the QuickBooks invoice ID, use it directly
      if (quickbooksInvoiceId) {
        const response = await getQuickBooksInvoice(quickbooksInvoiceId);
        console.log(response);
        // if (paymentLinkResponse.paymentUrl) {
        //   window.open(paymentLinkResponse.paymentUrl, "_blank");
        //   return;
        // }
      }

      // Otherwise, try to get the QuickBooks invoice details first
      const invoiceResponse = await getQuickBooksInvoice(invoiceId);
      if (invoiceResponse.success && invoiceResponse.Invoice?.Id) {
        const paymentLinkResponse = await getQuickBooksPaymentLink(
          invoiceResponse.Invoice.Id
        );
        if (paymentLinkResponse.paymentUrl) {
          window.open(paymentLinkResponse.paymentUrl, "_blank");
          return;
        }
      }

      console.error("Could not retrieve payment link for invoice");
    } catch (error) {
      console.error("Error getting payment link:", error);
    }
  };

  // Transform bookings with saved billing config or ESTIMATED services into estimate-like format
  const estimatesData = useMemo(() => {
    if (!bookingsData?.items) return { items: [] };

    // Group bookings that either:
    // 1. Have ESTIMATED services, OR
    // 2. Have a saved invoiceConfig (admin has saved billing configuration)
    // But exclude COMPLETED bookings (they should appear as invoices, not estimates)
    const bookingEstimates = new Map<string, {
      id: string;
      referenceNumber: string;
      estimateDate: Date;
      status: string;
      services: TServiceTicket[];
      booking: TBooking;
    }>();

    bookingsData.items.forEach((booking: TBooking) => {
      // Skip completed bookings - they would be invoiced
      if (booking.status === JobStatus.COMPLETED) return;

      const estimatedServices = booking.serviceTicket?.filter(
        (service: TServiceTicket) => service.status === JobStatus.ESTIMATED
      ) || [];

      // Get all non-completed services for bookings with saved config
      const scheduledServices = booking.serviceTicket?.filter(
        (service: TServiceTicket) =>
          service.status === JobStatus.SCHEDULED ||
          service.status === JobStatus.IN_PROGRESS
      ) || [];

      // Show in estimates if:
      // 1. Has estimated services, OR
      // 2. Has saved invoice config AND has services that aren't completed
      const hasInvoiceConfig = booking.invoiceConfig?.grandTotal !== undefined &&
                               booking.invoiceConfig.grandTotal > 0;
      const hasEstimatedServices = estimatedServices.length > 0;
      const hasActiveServices = scheduledServices.length > 0;

      if (hasEstimatedServices || (hasInvoiceConfig && (hasEstimatedServices || hasActiveServices))) {
        // Use all services if there's a saved config (show the full estimate)
        // Otherwise just show estimated services
        const servicesToShow = hasInvoiceConfig
          ? [...estimatedServices, ...scheduledServices]
          : estimatedServices;

        // Determine display status
        let displayStatus = "ESTIMATED";
        if (hasInvoiceConfig && !hasEstimatedServices && hasActiveServices) {
          displayStatus = "SCHEDULED";
        }

        bookingEstimates.set(booking.id, {
          id: booking.id,
          referenceNumber: booking.referenceNumber,
          estimateDate: booking.createdAt || booking.bookingDate,
          status: displayStatus,
          services: servicesToShow,
          booking: booking,
        });
      }
    });

    return {
      items: Array.from(bookingEstimates.values()).sort((a, b) => {
        const dateA = new Date(a.estimateDate).getTime();
        const dateB = new Date(b.estimateDate).getTime();
        return dateB - dateA; // Most recent first
      }),
    };
  }, [bookingsData]);

  const isLoading = isLoadingInvoices || isLoadingBookings;
  const error = invoicesError || bookingsError;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="animate-spin" />
        <span className="ml-2">Loading billing data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-700">
        <h3 className="font-semibold">Error loading billing data</h3>
        <p>Please try again later or contact support.</p>
      </div>
    );
  }

  // Helper function to calculate estimate total from services using the same logic as admin
  // If admin has saved a configuration with overrides, use those instead
  const calculateEstimateTotal = (estimate: { services: TServiceTicket[]; booking: TBooking }): number => {
    const { services, booking } = estimate;

    // If admin has saved a grandTotal in invoiceConfig, use that as the authoritative total
    if (booking.invoiceConfig?.grandTotal !== undefined && booking.invoiceConfig.grandTotal > 0) {
      return booking.invoiceConfig.grandTotal;
    }

    if (!services || services.length === 0) return 0;

    // Build service overrides map from saved config
    const serviceOverridesMap = new Map<string, any>();
    if (booking.invoiceConfig?.serviceOverrides) {
      booking.invoiceConfig.serviceOverrides.forEach((override) => {
        serviceOverridesMap.set(override.serviceId, override);
      });
    }

    // Calculate service costs with any saved overrides
    let total = services.reduce((sum, service) => {
      const cost = calculateServiceCost({
        service,
        artHandlerRole: artHandlerRole || undefined,
        addOnServices: addOnServices || [],
        serviceOverrides: serviceOverridesMap,
      });
      return sum + cost.total;
    }, 0);

    // Add admin charges if any
    if (booking.invoiceConfig?.adminCharges) {
      total += booking.invoiceConfig.adminCharges.reduce((sum, charge) => {
        const chargeInfo = adminChargesMap.get(charge?.chargeId);
        const chargeAmount = (chargeInfo?.rate || 0) * (charge?.quantity || 1);
        return sum + chargeAmount;
      }, 0);
    }

    // Add additional charges if any (quantity * rate)
    if (booking.invoiceConfig?.additionalCharges) {
      total += booking.invoiceConfig.additionalCharges.reduce((sum, charge) => {
        const chargeAmount = (Number(charge?.quantity) || 1) * (Number(charge?.rate) || 0);
        return sum + chargeAmount;
      }, 0);
    }

    return total;
  };

  // Helper to get service cost for a single service (using saved overrides if available)
  const getServiceCost = (service: TServiceTicket, booking: TBooking) => {
    // Build service overrides map from saved config
    const serviceOverridesMap = new Map<string, any>();
    if (booking.invoiceConfig?.serviceOverrides) {
      booking.invoiceConfig.serviceOverrides.forEach((override) => {
        serviceOverridesMap.set(override.serviceId, override);
      });
    }

    return calculateServiceCost({
      service,
      artHandlerRole: artHandlerRole || undefined,
      addOnServices: addOnServices || [],
      serviceOverrides: serviceOverridesMap,
    });
  };

  // Helper function to get status badge class
  const getEstimateStatusBadgeClass = (status: string): string => {
    switch (status) {
      case "ESTIMATED":
        return "bg-gray-200 text-[#1a1a2e]";
      case "SCHEDULED":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-200 text-[#1a1a2e]";
    }
  };

  // Helper function to format service type
  const formatServiceType = (serviceType: ServiceType): string => {
    return serviceType
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const hasData = (billingData?.invoices && billingData.invoices.length > 0) || (estimatesData?.items && estimatesData.items.length > 0);

  if (!hasData) {
    return (
      <div className={RESPONSIVE_CLASSES.container}>
        <h2 className={`${RESPONSIVE_CLASSES.titleText} font-semibold mb-2`}>
          Billing Summary
        </h2>
        <p className={`${RESPONSIVE_CLASSES.bodyText} text-gray-600 mb-4`}>
          View your account balance, estimates, and invoice history
        </p>
        <div className="bg-white p-8 border border-[#e5e5e5] rounded-lg text-center">
          <p className="text-[#1a1a2e] font-medium">You don't have any estimates or invoices yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={RESPONSIVE_CLASSES.container}>
      {/* Page Header */}
      <div className={RESPONSIVE_CLASSES.marginBottom}>
        <div className="border border-[#e5e5e5] rounded-lg inline-block px-4 py-2 mb-2 bg-white">
          <h2 className={`${RESPONSIVE_CLASSES.titleText} font-bold text-[#1a1a2e] uppercase tracking-wide !mb-0`}>
            Billing Summary
          </h2>
        </div>
        <p className={`${RESPONSIVE_CLASSES.bodyText} text-[#1a1a2e] font-medium mb-4`}>
          View your account balance, estimates, and invoice history
        </p>
      </div>

      {/* Account Summary */}
      {billingData && (
        <div className={RESPONSIVE_CLASSES.marginBottom}>
          <div className={RESPONSIVE_CLASSES.cardGrid}>
            <div className="bg-white border border-[#e5e5e5] rounded-lg p-3 sm:p-4">
              <div
                className={`${RESPONSIVE_CLASSES.captionText} text-gray-500 mb-1`}
              >
                Outstanding Balance
              </div>
              <div className={`${RESPONSIVE_CLASSES.headerText} font-semibold`}>
                ${billingData.account.balance.toFixed(2)}
              </div>
            </div>
            {billingData.account.overdue > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-lg p-3 sm:p-4">
                <div
                  className={`text-red-600 mb-1 ${RESPONSIVE_CLASSES.flexItemsCenter} ${RESPONSIVE_CLASSES.captionText}`}
                >
                  <AlertCircle size={16} className="mr-1 flex-shrink-0" />
                  Overdue Amount
                </div>
                <div
                  className={`${RESPONSIVE_CLASSES.headerText} font-semibold text-red-600`}
                >
                  ${billingData.account.overdue.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Estimates Section */}
      {estimatesData?.items && estimatesData.items.length > 0 && (
        <div className={RESPONSIVE_CLASSES.marginBottom}>
          <div className="flex justify-between items-center mb-4">
            <div className="border border-[#e5e5e5] rounded-lg inline-block px-3 py-1 bg-white">
              <h3 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide !mb-0">
                Estimates
              </h3>
            </div>
          </div>

          <div className="space-y-4">
            {estimatesData.items.map((estimate) => {
              const estimateTotal = calculateEstimateTotal(estimate);
              const isExpanded = expandedEstimate === estimate.id;

              return (
                <div key={estimate.id} className="bg-white border border-[#e5e5e5] rounded-lg">
                  {/* Estimate Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-[#fafafa]"
                    onClick={() =>
                      setExpandedEstimate(
                        expandedEstimate === estimate.id ? null : estimate.id
                      )
                    }
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        {isExpanded ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                        <div>
                          <div className="font-medium flex items-center">
                            <FileText size={16} className="mr-2" />
                            {estimate.referenceNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(estimate.estimateDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <div className="font-medium">
                            ${estimateTotal.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {estimate.services?.length || 0} service
                            {estimate.services?.length !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 border border-[#e5e5e5] rounded-lg text-sm font-bold uppercase ${getEstimateStatusBadgeClass(
                            estimate.status
                          )}`}
                        >
                          {estimate.status.charAt(0) +
                            estimate.status.slice(1).toLowerCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Estimate Details */}
                  {isExpanded && (
                    <div className="border-t border-[#e5e5e5] p-4 bg-white">
                      {/* Services */}
                      {estimate.services && estimate.services.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-sm uppercase tracking-wide mb-3">
                            Services
                          </h4>
                          <div className="space-y-3">
                            {estimate.services.map((service: TServiceTicket) => {
                              // Calculate service price using saved overrides if available
                              const cost = getServiceCost(service, estimate.booking);
                              const servicePrice = cost.total;

                              return (
                                <div
                                  key={service.id}
                                  className="border border-[#e5e5e5] rounded-lg p-3 bg-white"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <div className="font-medium">
                                        {formatServiceType(service.serviceType)}
                                      </div>
                                      {service.serviceDatetime && (
                                        <div className="text-sm text-gray-500 mt-1">
                                          Date:{" "}
                                          {new Date(
                                            service.serviceDatetime
                                          ).toLocaleDateString()}
                                        </div>
                                      )}
                                      {service.duration && (
                                        <div className="text-sm text-gray-500">
                                          Duration: {service.duration} hours
                                        </div>
                                      )}
                                      {service.staffs && service.staffs.length > 0 && (
                                        <div className="text-sm text-gray-500">
                                          Staff: {service.staffs.length}
                                        </div>
                                      )}
                                      {service.vessel && (
                                        <div className="text-sm text-gray-500">
                                          Vessel: {service.vessel.name}
                                        </div>
                                      )}
                                      {service.location && (
                                        <div className="text-sm text-gray-500">
                                          Location: {service.location.address ||
                                            `${service.location.city || ''} ${service.location.state || ''}`.trim() ||
                                            'N/A'}
                                        </div>
                                      )}
                                      {service.scope && (
                                        <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-300">
                                          {service.scope}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <div className="font-semibold">
                                        ${servicePrice.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Admin Charges (standard fees like rush fee, etc.) */}
                      {estimate.booking.invoiceConfig?.adminCharges &&
                       estimate.booking.invoiceConfig.adminCharges.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-sm uppercase tracking-wide mb-3">
                            Admin Charges
                          </h4>
                          <div className="space-y-2">
                            {estimate.booking.invoiceConfig.adminCharges
                              .filter((charge) => charge && charge.chargeId)
                              .map((charge) => {
                                const chargeInfo = adminChargesMap.get(charge.chargeId);
                                const chargeAmount = (chargeInfo?.rate || 0) * (charge.quantity || 1);
                                return (
                                  <div
                                    key={charge.chargeId}
                                    className="border border-[#e5e5e5] rounded-lg p-3 bg-white flex justify-between items-center"
                                  >
                                    <div>
                                      <div className="font-medium">{chargeInfo?.name || 'Admin Charge'}</div>
                                      {charge.quantity > 1 && (
                                        <div className="text-xs text-gray-500">
                                          Qty: {charge.quantity} × ${(chargeInfo?.rate || 0).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                    <div className="font-semibold">${chargeAmount.toFixed(2)}</div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {/* Additional Charges (custom line items added by admin) */}
                      {estimate.booking.invoiceConfig?.additionalCharges &&
                       estimate.booking.invoiceConfig.additionalCharges.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-sm uppercase tracking-wide mb-3">
                            Additional Charges
                          </h4>
                          <div className="space-y-2">
                            {estimate.booking.invoiceConfig.additionalCharges
                              .filter((charge) => charge && (charge.rate !== undefined || charge.quantity !== undefined))
                              .map((charge) => {
                                const chargeAmount = (Number(charge.quantity) || 1) * (Number(charge.rate) || 0);
                                return (
                                  <div
                                    key={charge.id || charge.description}
                                    className="border border-[#e5e5e5] rounded-lg p-3 bg-white flex justify-between items-center"
                                  >
                                    <div>
                                      <div className="font-medium">{charge.description || 'Additional Charge'}</div>
                                      {charge.notes && (
                                        <div className="text-xs text-gray-500">{charge.notes}</div>
                                      )}
                                      {(charge.quantity > 1 || charge.date) && (
                                        <div className="text-xs text-gray-500">
                                          {charge.quantity > 1 && `Qty: ${charge.quantity} × $${(Number(charge.rate) || 0).toFixed(2)}`}
                                          {charge.quantity > 1 && charge.date && ' • '}
                                          {charge.date && `Date: ${charge.date}`}
                                        </div>
                                      )}
                                    </div>
                                    <div className="font-semibold">${chargeAmount.toFixed(2)}</div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {/* Estimate Total */}
                      <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
                        <div className="flex justify-between items-center">
                          <div className="font-bold text-lg">Estimated Total</div>
                          <div className="font-bold text-xl">
                            ${estimateTotal.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invoices Section */}
      {billingData && billingData.invoices.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="border border-[#e5e5e5] rounded-lg inline-block px-3 py-1 bg-white">
              <h3 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide !mb-0">
                Invoices
              </h3>
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-bold text-sm uppercase bg-white focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="year">Past Year</option>
              <option value="6months">Past 6 Months</option>
              <option value="3months">Past 3 Months</option>
            </select>
          </div>

        <div className="space-y-4">
          {billingData.invoices.map((invoice) => (
            <div key={invoice.id} className="bg-white border border-[#e5e5e5] rounded-lg">
              {/* Invoice Header */}
              <div
                className="p-4 cursor-pointer hover:bg-[#fafafa]"
                onClick={() =>
                  setExpandedInvoice(
                    expandedInvoice === invoice.id ? null : invoice.id
                  )
                }
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    {expandedInvoice === invoice.id ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                    <div>
                      <div className="font-medium flex items-center">
                        {invoice.id}
                        {invoice.bookingRef && (
                          <span className="text-gray-500 text-sm ml-2">
                            ({invoice.bookingRef})
                          </span>
                        )}
                      </div>
                      {invoice.project && (
                        <div className="text-sm text-gray-500">
                          {invoice.project}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="font-medium">
                        ${invoice.amount.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Due: {invoice.dueDate}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 border border-[#e5e5e5] rounded-lg text-sm font-bold uppercase ${
                        invoice.status === "paid"
                          ? "bg-[#1a1a2e] text-white"
                          : invoice.status === "overdue"
                          ? "bg-[#1a1a2e] text-white"
                          : "bg-white text-[#1a1a2e]"
                      }`}
                    >
                      {invoice.status.charAt(0).toUpperCase() +
                        invoice.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              {expandedInvoice === invoice.id && (
                <div className="border-t border-[#e5e5e5] p-4 bg-white">
                  {/* Line Items */}
                  <div className="mb-4">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-[#1a1a2e] text-sm font-bold uppercase border-b-2 border-black">
                          <th className="pb-2">Date</th>
                          <th className="pb-2">Description</th>
                          <th className="pb-2 text-right">Quantity</th>
                          <th className="pb-2 text-right">Rate</th>
                          <th className="pb-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {invoice.items.map((item, index) => (
                          <tr key={index} className="border-b-2 border-black">
                            <td className="py-2">{item.serviceDate ?? "-"}</td>
                            <td className="py-2">{item.description}</td>
                            <td className="py-2 text-right">{item.quantity}</td>
                            <td className="py-2 text-right">
                              ${Number(item.rate).toFixed(2)}
                            </td>
                            <td className="py-2 text-right">
                              ${Number(item.amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-[#e5e5e5] font-bold">
                          <td colSpan={4} className="py-2">
                            Total
                          </td>
                          <td className="py-2 text-right">
                            ${invoice.amount.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Invoice Notes */}
                  {invoice.notes && invoice.notes.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-[#e5e5e5]">
                      <div className="text-sm text-gray-600">
                        {invoice.notes.map((note, index) => (
                          <div key={index} className="mb-1">
                            • {note}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-2">
                    <div className="text-sm text-gray-500">
                      {invoice.status === "paid"
                        ? `Paid on ${invoice.paidDate}`
                        : `Due by ${invoice.dueDate}`}
                    </div>
                    <div className="space-x-3">
                      <button
                        className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] hover:bg-[#1a1a2e] hover:text-white px-4 py-2 font-bold text-sm uppercase tracking-wide transition-all"
                        onClick={() => handleDownloadPdf(invoice.id)}
                      >
                        Download PDF
                      </button>
                      {(invoice.status === "unpaid" ||
                        invoice.status === "overdue") && (
                        <button
                          className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white hover:bg-white hover:text-[#1a1a2e] px-4 py-2 font-bold text-sm uppercase tracking-wide transition-all flex items-center"
                          onClick={() =>
                            handleGetPaymentLink(invoice.id, invoice.quickbooksInvoiceId)
                          }
                        >
                          Pay Now
                          <ExternalLink size={16} className="ml-1" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
};

export default BillingView;
